<template>
  <div class="attendance-container">
    <!-- 顶部搜索 -->
    <div class="search-bar">
      <el-input
        placeholder="搜索学员姓名/手机号"
        v-model="searchText"
        class="search-input"
        clearable
        @clear="handleSearch"
        @keydown.native.enter="handleSearch"
      />
      <el-button type="primary" class="search-btn" @click="handleSearch">搜索</el-button>
    </div>

    <!-- 筛选条 -->
    <div class="filter-bar">
      <el-select
        v-model="searchForm.type"
        placeholder="会员类型"
        clearable
        class="filter-select"
        @change="handleSearch"
      >
        <el-option label="课时包" value="package"></el-option>
        <el-option label="包月包" value="monthly"></el-option>
      </el-select>
      
      <el-select
        v-model="searchForm.courseId"
        placeholder="课程"
        clearable
        class="filter-select"
        @change="handleSearch"
      >
        <el-option
          v-for="course in courseOptions"
          :key="course._id"
          :label="course.name"
          :value="course._id"
        ></el-option>
      </el-select>
    </div>

    <!-- 会员列表卡片 -->
    <div class="member-list">
      <div v-for="member in paginatedMembers" :key="member._id" class="member-card">
        <div class="card-header">
          <div class="card-title">
            <h3>{{ member.xueyuanName }}</h3>
            <el-tag :type="member.type === 'package' ? 'success' : 'primary'">
              {{ member.type === 'package' ? '课时包' : '包月包' }}
            </el-tag>
          </div>
          <p class="card-subtitle">{{ member.phone }} | {{ member.courseName }}</p>
        </div>

        <div class="card-body">
          <div class="card-remaining">
            <span class="remaining-label">剩余：</span>
            <span class="remaining-value">
              {{ member.type === 'package' ? `${member.packageCount}课时` : `${member.remainingDays}天` }}
            </span>
          </div>
          
          <div class="card-details">
            <div>
              <p class="detail-label">总</p>
              <p class="detail-value">
                {{ member.type === 'package' ? `${member.totalLessons || 0}课时` : `${member.totalDays || 0}天` }}
              </p>
            </div>
            <div>
              <p class="detail-label">已用</p>
              <p class="detail-value">
                {{ member.type === 'package' 
                  ? `${(member.totalLessons || 0) - member.packageCount}课时` 
                  : `${(member.totalDays || 0) - member.remainingDays}天` }}
              </p>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <el-button
            type="primary"
            class="checkin-btn"
            @click="handleCheckIn(member)"
            :disabled="member.type === 'package' && member.packageCount <= 0"
          >
            {{ member.type === 'package' ? '点名消课' : '包月无需点名' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 无数据提示 -->
    <div v-if="paginatedMembers.length === 0 && !loading" class="empty-tip">
      <p>暂无匹配学员</p>
    </div>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        small
        layout="prev, pager, next"
        :current-page="memberCurrentPage"
        :page-size="memberPageSize"
        :total="filteredMembers.length"
        @current-change="handleMemberCurrentChange"
      />
    </div>

    <!-- 点名消课弹窗 -->
    <el-dialog
      title="点名消课"
      :visible.sync="checkInDialogVisible"
      width="90%"
      center
    >
      <el-form :model="checkInForm" label-width="70px">
        <el-form-item label="学员">
          <el-input v-model="checkInForm.xueyuanName" disabled />
        </el-form-item>
        <el-form-item label="课程">
          <el-input v-model="checkInForm.courseName" disabled />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="checkInForm.status">
            <el-radio label="present">出勤</el-radio>
            <el-radio label="absent">缺勤</el-radio>
            <el-radio label="leave">请假</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="消课" v-if="checkInForm.memberType === 'package'">
          <el-input-number
            v-model="checkInForm.lessonCount"
            :min="1"
            :max="checkInForm.maxLessons || 999"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input type="textarea" v-model="checkInForm.remark" />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="checkInDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitCheckIn">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      memberList: [],
      courseOptions: [],
      searchText: '',
      searchForm: {
        type: '',
        courseId: ''
      },
      checkInDialogVisible: false,
      checkInForm: {
        memberId: '',
        xueyuanName: '',
        courseName: '',
        status: 'present',
        remark: '',
        lessonCount: 1,
        memberType: '',
        maxLessons: 0
      },
      memberCurrentPage: 1,
      memberPageSize: 10
    }
  },
  computed: {
    filteredMembers() {
      return this.memberList.filter(member => {
        const matchesSearch = !this.searchText
          || member.xueyuanName.includes(this.searchText)
          || member.phone.includes(this.searchText);
          
        const matchesType = !this.searchForm.type || member.type === this.searchForm.type;
        const matchesCourse = !this.searchForm.courseId || member.courseId === this.searchForm.courseId;
        
        return matchesSearch && matchesType && matchesCourse;
      });
    },
    paginatedMembers() {
      const start = (this.memberCurrentPage - 1) * this.memberPageSize;
      const end = start + this.memberPageSize;
      return this.filteredMembers.slice(start, end);
    }
  },
  created() {
    this.fetchMembers();
    this.fetchCourses();
  },
  methods: {
    fetchMembers() {
      this.loading = true;
      this.$http.get('/api/members')
        .then(response => {
          this.memberList = response.data.data;
        })
        .catch(error => {
          console.error('获取会员列表失败:', error);
          this.$message.error('获取会员列表失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchCourses() {
      this.$http.get('/api/courses')
        .then(response => {
          this.courseOptions = response.data.data.list;
        })
        .catch(error => {
          console.error('获取课程列表失败:', error);
          this.$message.error('获取课程列表失败');
        });
    },
    handleSearch() {
      this.memberCurrentPage = 1;
    },
    handleCheckIn(row) {
      this.checkInForm = {
        memberId: row._id,
        xueyuanName: row.xueyuanName,
        courseName: row.courseName,
        status: 'present',
        remark: '',
        lessonCount: 1,
        memberType: row.type,
        maxLessons: row.packageCount
      };
      this.checkInDialogVisible = true;
    },
    submitCheckIn() {
      this.$http.post('/api/attendances', this.checkInForm)
        .then(response => {
          this.$message.success(response.data.message);
          this.checkInDialogVisible = false;
          this.fetchMembers();
        })
        .catch(error => {
          this.$message.error(error.response?.data?.message || '消课失败');
        });
    },
    handleMemberCurrentChange(val) {
      this.memberCurrentPage = val;
      window.scrollTo(0, 0);
    }
  }
}
</script>

<style scoped>
.attendance-container {
  padding: 12px;
  background: #f5f7fa;
  min-height: 100vh;
}

/* 搜索栏 */
.search-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.search-input {
  flex: 1;
}
.search-btn {
  width: 90px;
}

/* 筛选条 */
.filter-bar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.filter-select {
  flex: 1;
}

/* 会员卡片列表 */
.member-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.member-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.card-header {
  margin-bottom: 10px;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.card-title h3 {
  font-size: 16px;
  font-weight: 600;
}
.card-subtitle {
  color: #909399;
  font-size: 13px;
}

/* 卡片内容 */
.card-body {
  margin-bottom: 14px;
}
.card-remaining {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
}
.remaining-label {
  color: #909399;
  font-size: 13px;
}
.remaining-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.card-details {
  display: flex;
  gap: 14px;
}
.card-details > div {
  flex: 1;
  text-align: center;
}
.detail-label {
  color: #909399;
  font-size: 12px;
  margin-bottom: 4px;
}
.detail-value {
  font-size: 14px;
  font-weight: 500;
}

/* 卡片底部按钮 */
.card-footer {
  display: flex;
}
.checkin-btn {
  flex: 1;
}

/* 无数据提示 */
.empty-tip {
  text-align: center;
  padding: 40px 10px;
  color: #909399;
}

/* 分页 */
.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}
</style>